import { HttpClient } from '@angular/common/http';
import {
    EnvironmentProviders,
    inject,
    InjectionToken,
    makeEnvironmentProviders,
    provideAppInitializer
} from '@angular/core';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { firstValueFrom, timeout, catchError, of, filter, switchMap, take } from 'rxjs';

import {
    FlagConfig,
    FlagValueOf,
    UiFlagKeyOf,
    UiFlagValues,
    uiFlagDefaults,
    uiFlagKeysOf
} from '@juno/shared/application/feature-flags';

import { environment } from '../../../environments/environment';

/**
 * How long (ms) the bootstrap fetch may take before the SPA falls back to
 * catalog `defaultValue` defaults.
 */
const FETCH_TIMEOUT_MS = 5_000;

/**
 * Injection token for the resolved UI flag store.
 *
 * The token is typed against the open `FlagConfig` surface. Within the app
 * composition root, `provideFeatureFlags<typeof FLAG_CATALOG>(...)` narrows the
 * stored value at the provider site so that consumers get the closed `ui.*` key
 * union. This token is the SINGLE instance used by both the provider and the
 * consumer — Angular DI compares by reference identity, not by description.
 */
export const UI_FEATURE_FLAGS = new InjectionToken<UiFlagStore>('UI_FEATURE_FLAGS');

/**
 * Synchronous, type-safe read of the resolved UI flags.
 *
 * Constructed once at bootstrap with the fetched (or defaulted) values, then
 * provided via the {@link UI_FEATURE_FLAGS} token.
 */
export class UiFlagStore<Catalog extends Readonly<Record<string, FlagConfig>> = Readonly<Record<string, FlagConfig>>> {
    constructor(private readonly values: UiFlagValues<Catalog>) {
        Object.freeze(this.values);
    }

    /** Read a single `ui.*` flag value, synchronously. */
    getFlag<Key extends UiFlagKeyOf<Catalog>>(key: Key): FlagValueOf<Catalog[Key]> {
        return this.values[key];
    }

    /** The full snapshot, for debug / logging. */
    toJSON(): UiFlagValues<Catalog> {
        return this.values;
    }
}

// Module-level holder — written by the initializer, read by the factory.
let _resolvedStore: UiFlagStore | undefined;

/**
 * Wire the UI feature-flag bootstrap into the app's provider array.
 *
 * Returns an `EnvironmentProviders` bundle that:
 * 1. Registers a `provideAppInitializer` that waits for MSAL to reach
 *    `InteractionStatus.None` (redirect handled, active account set), then
 *    fetches `GET /v3/feature-flags`.
 * 2. Provides the `UI_FEATURE_FLAGS` token with the resolved store.
 *
 * Angular's `APP_INITIALIZER` providers run concurrently (Promise.all), so the
 * flag fetch cannot rely on provider-array ordering to sequence after MSAL.
 * Instead, the initializer subscribes to `MsalBroadcastService.inProgress$` and
 * waits for `None` before issuing the HTTP request.
 *
 * On fetch failure or timeout, catalog `defaultValue` defaults from the catalog
 * are used — the SPA never blocks on a source outage.
 *
 * @param catalog the composed `FLAG_CATALOG`, imported at the app's composition root.
 */
export function provideFeatureFlags<Catalog extends Readonly<Record<string, FlagConfig>>>(
    catalog: Catalog
): EnvironmentProviders {
    return makeEnvironmentProviders([
        provideAppInitializer(() => {
            const http = inject(HttpClient);
            const msalBroadcast = inject(MsalBroadcastService);
            const defaults = uiFlagDefaults(catalog);

            const apiEntry =
                environment.apiConfig.find((c: { scopes: string[] }) =>
                    c.scopes.some((s: string) => s.includes('juno'))
                ) ?? environment.apiConfig[1];
            const apiBaseUrl = apiEntry.uri.replace('/*', '');

            // Wait for MSAL to finish its redirect handling and reach an idle
            // interaction status. This guarantees the active account is set and
            // the MSAL interceptor will attach a valid token to the fetch.
            return firstValueFrom(
                msalBroadcast.inProgress$.pipe(
                    filter((status: InteractionStatus) => status === InteractionStatus.None),
                    take(1),
                    switchMap(() =>
                        http.get<Record<string, boolean | string>>(`${apiBaseUrl}/v3/feature-flags`).pipe(
                            timeout(FETCH_TIMEOUT_MS),
                            catchError(() => of(null))
                        )
                    )
                )
            ).then((response: Record<string, boolean | string> | null) => {
                if (response) {
                    const merged: Record<string, boolean | string> = { ...defaults };
                    const uiKeys = new Set(uiFlagKeysOf(catalog) as string[]);
                    for (const [key, value] of Object.entries(response)) {
                        if (uiKeys.has(key)) {
                            merged[key] = value;
                        }
                    }
                    _resolvedStore = new UiFlagStore(merged as UiFlagValues<Catalog>);
                } else {
                    _resolvedStore = new UiFlagStore(defaults);
                }
            });
        }),
        {
            provide: UI_FEATURE_FLAGS,
            useFactory: (): UiFlagStore<Catalog> => {
                if (!_resolvedStore) {
                    throw new Error('UI_FEATURE_FLAGS accessed before the bootstrap initializer completed.');
                }
                return _resolvedStore as UiFlagStore<Catalog>;
            }
        }
    ]);
}
